import { ChevronRight } from 'lucide-react'
import type { Lead } from '../../types/crm'
import { displayValue, formatLocation } from '../../utils/format'
import { hasMeaningfulValue } from './LeadDetailModal'
import { getLeadContact, getLeadDisplayName, getLeadSubtitle, type LeadContact } from './leadDisplay'
import { LeadWebsiteLogo } from './LeadWebsiteLogo'
import { getLeadNeeds, getLeadWebsite } from './unifiedLeadFields'

function getLeadNotes(lead: Lead): string {
  // A prospecting-only lead has no CRM notes, so what it needs is shown instead.
  const notes = [lead.opportunity?.notes, lead.organization?.notes, getLeadNeeds(lead)]

  for (const value of notes) {
    const normalized = value?.trim() ?? ''
    const placeholder = normalized.toLowerCase().replace(/[.!]+$/, '')
    if (hasMeaningfulValue(normalized) && placeholder !== 'not provided') return normalized
  }

  return ''
}

function LeadContactValue({ contact, className = '' }: { contact: LeadContact; className?: string }) {
  if (contact.kind === 'none') return <span className={className}> - </span>

  const action = contact.kind === 'phone' ? 'Call' : 'Email'
  return (
    <a
      aria-label={`${action} ${contact.value}`}
      className={className}
      href={contact.href}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {contact.value}
    </a>
  )
}

export function LeadTable<TLead extends Lead>({
  leads,
  selectedId,
  onSelect,
  assignmentOf,
}: {
  leads: TLead[]
  selectedId?: string
  onSelect: (lead: TLead, trigger: HTMLElement) => void
  /** The CRM salesperson line for a lead, or null while it is unknown. */
  assignmentOf?: (lead: TLead) => string | null
}) {
  return (
    <>
      <div className="lead-table-wrap">
        <div className="lead-table" role="table" aria-label="Leads">
          <div className="lead-table-header" role="row">
            <span role="columnheader">Lead</span>
            <span role="columnheader">Priority</span>
            <span role="columnheader">Stage</span>
            <span role="columnheader">Account manager</span>
            <span role="columnheader">Location</span>
            <span role="columnheader">Contact</span>
          </div>
          <div className="lead-table-body" role="rowgroup">
            {leads.map((lead) => {
              const displayName = getLeadDisplayName(lead)
              const preferredContact = getLeadContact(lead)
              const subtitle = getLeadSubtitle(lead)
              const assignment = assignmentOf?.(lead) ?? null
              return (
                <div
                  aria-label={`Open ${displayName} details`}
                  className={`lead-card-row${selectedId === lead.id ? ' is-selected' : ''}`}
                  key={lead.id}
                  onClick={(event) => onSelect(lead, event.currentTarget)}
                  role="row"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onSelect(lead, event.currentTarget)
                    }
                  }}
                >
                  <div className="lead-grid-cell" role="cell">
                    <div className="lead-company-cell">
                      <LeadWebsiteLogo website={getLeadWebsite(lead)} />
                      <div className="lead-company-copy">
                        <strong>{displayName}</strong>
                        <span>{subtitle}</span>
                      </div>
                    </div>
                  </div>
                  <div className="lead-grid-cell" role="cell">{lead.priority}</div>
                  <div className="lead-grid-cell" role="cell">
                    {lead.opportunity ? `${displayValue(lead.opportunity.status, ' - ')} · ${displayValue(lead.opportunity.stage, ' - ')}` : 'No opportunity'}
                  </div>
                  <div className="lead-grid-cell" role="cell">{displayValue(lead.organization?.accountManager ?? lead.opportunity?.dealOwner)}</div>
                  <div className="lead-grid-cell" role="cell">{formatLocation(lead.organization?.city ?? '', lead.organization?.state ?? '')}</div>
                  <div className="lead-grid-cell contact-cell" role="cell">
                    <span className="lead-contact-stack">
                      <LeadContactValue contact={preferredContact} />
                      {assignment ? <span className="lead-assignment">{assignment}</span> : null}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <ul className="mobile-lead-list">
        {leads.map((lead) => {
          const displayName = getLeadDisplayName(lead)
          const notes = getLeadNotes(lead)
          const subtitle = getLeadSubtitle(lead)
          const assignment = assignmentOf?.(lead) ?? null
          return (
            <li key={lead.id}>
              <div
                aria-label={`Open ${displayName} details`}
                className={`mobile-lead-row${selectedId === lead.id ? ' is-selected' : ''}`}
                onClick={(event) => onSelect(lead, event.currentTarget)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect(lead, event.currentTarget)
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span className="mobile-lead-identity">
                  <LeadWebsiteLogo website={getLeadWebsite(lead)} />
                  <span className="mobile-lead-main">
                    <strong>{displayName}</strong>
                    <span>{subtitle}</span>
                    <small>{formatLocation(lead.organization?.city ?? '', lead.organization?.state ?? '')}</small>
                    {assignment ? <small className="lead-assignment">{assignment}</small> : null}
                    {notes ? <span className="mobile-lead-notes">{notes}</span> : null}
                  </span>
                </span>
                <span className="mobile-lead-meta"><i>{lead.priority}</i><span>{lead.opportunity ? `${lead.opportunity.status} · ${lead.opportunity.stage}` : 'No opportunity'}</span></span>
                <ChevronRight aria-hidden="true" size={20} />
              </div>
            </li>
          )
        })}
      </ul>
    </>
  )
}
